import type { TranslationKey } from '@/i18n/types'
import { activationStore } from '@/stores/activation.store'
import { authStore } from '@/stores/auth.store'
import { createSignal } from 'solid-js'

export type OnboardingPhase = 'verify' | 'upload' | 'checklist'

export type JourneyStepKey = 'account' | 'email' | 'cv' | 'jobSought' | 'companion' | 'focus'

export interface JourneyStep {
  key: JourneyStepKey
  done: boolean
}

const [manuallyOpened, setManuallyOpened] = createSignal(false)

export function resetOnboardingSession(): void {
  setManuallyOpened(false)
}

function hasBlockingWork(): boolean {
  return !authStore.isEmailVerified || activationStore.isLocked()
}

export const onboardingStore = {
  phase(candidateId: number | null): OnboardingPhase {
    if (candidateId === null || !activationStore.isLoaded() || !hasBlockingWork()) {
      return 'checklist'
    }
    if (!authStore.isEmailVerified) {
      return 'verify'
    }
    if (activationStore.isLocked()) {
      return 'upload'
    }
    return 'checklist'
  },

  isBlocking(candidateId: number | null): boolean {
    return onboardingStore.phase(candidateId) !== 'checklist'
  },

  hasJobSought(): boolean {
    return activationStore.hasDesiredRole()
  },

  hasCompanionRefinement(): boolean {
    return !activationStore.needsDiscovery()
  },

  companionInJourney(): boolean {
    return activationStore.discoveryRequired() || activationStore.discoveryDone()
  },

  companionOutstanding(): boolean {
    return (
      !onboardingStore.hasCompanionRefinement() ||
      activationStore.nextActions().some((a) => a.id === 'companion_discovery')
    )
  },

  hasFocus(): boolean {
    return activationStore.focusAnswered()
  },

  journeySteps(): JourneyStep[] {
    const steps: JourneyStep[] = [
      { key: 'account', done: authStore.state.isAuthenticated },
      { key: 'email', done: authStore.isEmailVerified },
      { key: 'cv', done: !activationStore.isLocked() },
      { key: 'jobSought', done: onboardingStore.hasJobSought() },
    ]
    if (onboardingStore.companionInJourney()) {
      steps.push({ key: 'companion', done: onboardingStore.hasCompanionRefinement() })
    }
    steps.push({ key: 'focus', done: onboardingStore.hasFocus() })
    return steps
  },

  currentStepIndex(): number {
    const steps = onboardingStore.journeySteps()
    const next = steps.findIndex((s) => !s.done)
    return next === -1 ? steps.length : next
  },

  currentStep(): number {
    return onboardingStore.currentStepIndex() + 1
  },

  hasOutstanding(candidateId: number | null): boolean {
    return (
      onboardingStore.isBlocking(candidateId) ||
      !onboardingStore.hasJobSought() ||
      onboardingStore.companionOutstanding() ||
      !onboardingStore.hasFocus()
    )
  },

  isOpen(candidateId: number | null): boolean {
    return onboardingStore.isBlocking(candidateId) || manuallyOpened()
  },

  close() {
    setManuallyOpened(false)
  },

  open() {
    setManuallyOpened(true)
  },

  nextStepKey(candidateId: number | null): TranslationKey {
    switch (onboardingStore.phase(candidateId)) {
      case 'verify':
        return 'onboarding.next.verify'
      case 'upload':
        return 'onboarding.next.cv'
      default:
        if (!onboardingStore.hasJobSought()) {
          return 'onboarding.next.jobSought'
        }
        return onboardingStore.companionOutstanding()
          ? 'onboarding.next.companion'
          : 'onboarding.next.focus'
    }
  },

  remaining(): number {
    let left = 0
    if (!authStore.isEmailVerified) {
      left++
    }
    if (activationStore.isLocked()) {
      left++
    }
    if (!onboardingStore.hasJobSought()) {
      left++
    }
    if (onboardingStore.companionOutstanding()) {
      left++
    }
    if (!onboardingStore.hasFocus()) {
      left++
    }
    return left
  },
}
